import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import ImmutablePropTypes from 'react-immutable-proptypes';
import { TextButton } from '@bibliocommons/base-buttons';
import { Plus, Checkmark } from '@bibliocommons/base-icons';
import { listShape } from '@bibliocommons/bc-prop-types';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import ListsActions from 'app/actions/ListsActions';
import { UI_COMPONENT_TYPE } from 'app/constants/AnalyticsGa4Constants';
import { selectListItemTransactions } from 'app/selectors/ListsSelector';
import './AddToListButton.scss';

export default function AddToListButton({ bibTitle, existingListItem, list, metadataId, trackClick }) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const listItemTransactions = useSelector(selectListItemTransactions);

  const title = bibTitle;
  const listName = list.get('name');

  function renderContent() {
    if (existingListItem) {
      const isUnpublishedItem = list.get('draftListId') && existingListItem.get('draft');
      return (
        <>
          <span className="title-added-label" aria-hidden>
            <Checkmark />
            <FormattedMessage id={isUnpublishedItem ? 'on_unpublished_draft_list' : 'on_list'} />
          </span>
          <ScreenReaderMessage>
            <FormattedMessage
              id={isUnpublishedItem ? 'on_unpublished_draft_list_sr' : 'on_list_sr'}
              values={{ title, listName }}
            />
          </ScreenReaderMessage>
        </>
      );
    }

    const addToExisting = intl.formatMessage({ id: 'button_add_to_existing_draft_list' });
    const addToNew = intl.formatMessage({ id: 'button_add_to_new_draft_list' });
    const addToExistingSR = intl.formatMessage({ id: 'button_add_to_existing_draft_list_sr' }, { title, listName });
    const addToNewSR = intl.formatMessage({ id: 'button_add_to_new_draft_list_sr' }, { title, listName });

    const existingDraft = list.get('isDraft') || !!list.get('draftListId');
    const buttonText = existingDraft ? addToExisting : addToNew;
    const srButtonText = existingDraft ? addToExistingSR : addToNewSR;

    function handleAddToList() {
      const uiEntity = {
        ui_component_label: buttonText,
        ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT
      };

      trackClick({ uiEntity });
      dispatch(ListsActions.createListItem({ metadataId, listId: list.get('id') }));
    }

    return (
      <TextButton
        handleClick={handleAddToList}
        renderLeadingIcon={() => <Plus />}
        isLoading={listItemTransactions.getIn([list.get('id'), 'isAdding'])}
        dataKey="add-to-list-button"
      >
        <span aria-hidden>{buttonText}</span>
        <ScreenReaderMessage>{srButtonText}</ScreenReaderMessage>
      </TextButton>
    );
  }

  return <div className="cp-add-to-list-button">{renderContent()}</div>;
}

AddToListButton.propTypes = {
  bibTitle: PropTypes.string.isRequired,
  existingListItem: ImmutablePropTypes.map,
  list: listShape.isRequired,
  metadataId: PropTypes.string.isRequired,
  trackClick: PropTypes.func.isRequired
};
